import React, { useState } from "react";
import { Box, CircularProgress, Grid, Stack, Typography } from "@mui/material";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import DashboardPanelHeader from "components/DashboardPanelHeader";
import LeaseListPopUp from "./leaseListPopUp";

const ActiveLeadsMixByTemperature = ({ data, isError, isLoading }) => {
    const [status, setStatus] = useState("");
    const [header, setHeader] = useState("");
    const [isPopup, setIsPopup] = useState(false);

    const handleClick = (key, label) => {
        const filters = {
            status: key,
        };

        setStatus(filters);
        setHeader(label);
        setIsPopup(true);
    };

    const handleClose = () => setIsPopup(false);

    const apiData = data?.activeLeadMixByTemperature || {};

    const colorMap = {
        Won: "#22C55E",
        Lost: "#DC2626",
        Hot: "#ff6a00",
        Warm: "#ffba44",
        Cold: "#3B82F6",
    };

    const items = Object.entries(apiData).map(([key, value]) => ({
        key,
        label: `${key} Leads`,
        value,
        color: colorMap[key] || "#888",
    }));
    const totalCount = items.reduce((acc, cur) => acc + cur.value, 0);

    const getCenterTemplate = (val) => `
    <div style="
      text-align:center;
      font-family:Outfit;
    ">
      <span style="
        font-size:22px;
        font-weight:700;
        color:#000000;
      ">
        ${val}
      </span>
      <br/>
      <span style="
        font-size:12px;
        color:#7D7F81;
      ">
        Total Leads
      </span>
    </div>
  `;

    const options = {
        chart: {
            type: "pie",
            height: 200,
            backgroundColor: "transparent",
            margin: 0,
        },
        title: {
            text: getCenterTemplate(totalCount),
            useHTML: true,
            align: "center",
            verticalAlign: "middle",
            y: 25,
        },

        tooltip: {
            enabled: true,
            useHTML: true,
            formatter: function () {
                return `
          <div style="font-family:Outfit;font-size:12px;padding:4px">
            <span style="color:${this.point.color}">●</span>
            <b> ${this.point.name}</b>: ${this.point.y}
          </div>
        `;
            },
        },

        plotOptions: {
            pie: {
                innerSize: "75%",
                size: "100%",
                borderWidth: 0,
                borderRadius: 0,
                dataLabels: {
                    enabled: false,
                },
                cursor: "pointer",
                point: {
                    events: {
                        click: function () {
                            handleClick(this.options.key, this.name);
                        }
                    }
                }
            },
        },

        series: [
            {
                name: "Leads",
                data: items.map((i) => ({
                    name: i.label,
                    y: i.value,
                    color: i.color,
                    key: i.key,
                })),
            },
        ],

        credits: {
            enabled: false,
        },

        legend: {
            enabled: false,
        },
    };

    return (
        <>
            <Box
                sx={{
                    background: "#fff",
                    border: "1px solid #E4E4E4",
                    borderRadius: 1,
                    width: "100%",
                }}
            >
                <DashboardPanelHeader eyebrow="Pipeline quality" title="Lead Temperature Mix" subtitle="See how active leads are distributed across engagement and conversion readiness." />
                <Box
                    sx={{
                        borderBottom: "1px solid #E4E4E4",
                        p: 1.5,
                        display: "none",
                    }}
                >
                    <Typography
                        sx={{
                            fontFamily: "Outfit",
                            fontWeight: 600,
                            fontSize: "20px",
                        }}
                    >
                        Total Leads Mix by Temperature
                    </Typography>
                    <Typography
                        sx={{
                            fontFamily: "Outfit",
                            fontWeight: 300,
                            fontSize: "14px",
                            color: "#7D7F81",
                        }}
                    >
                        Distribution of total leads based on temperature classification
                    </Typography>
                </Box>
                {isLoading && (
                    <Box
                        sx={{
                            minHeight: "100px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                        }}
                    >
                        <CircularProgress size={32} />
                    </Box>
                )}
                {isError && (
                    <Box
                        sx={{
                            minHeight: "100px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            px: 2,
                        }}
                    >
                        <Typography
                            sx={{
                                fontFamily: "Outfit, sans-serif",
                                fontSize: "14px",
                                color: "#FF4F8B",
                                textAlign: "center",
                            }}
                        >
                            Failed to load data. Please try again.
                        </Typography>
                    </Box>
                )}
                {!isError && !isLoading && (
                    <Box
                        sx={{
                            display: "flex",
                            width: "100%",
                            minHeight: 235,
                            px: 2,
                            py: 2,
                            boxSizing: "border-box",
                        }}
                    >
                        <Box sx={{ width: "40%" }}>
                            <Stack
                                spacing={2.4}
                                sx={{
                                    width: "100%",
                                    pt: 1.5,
                                    pl: 1.5,
                                    pb: 1.5,
                                    maxHeight: "235px",
                                    minHeight: "235px",
                                    overflowY: "auto",
                                    // justifyContent: "center",
                                }}
                            >
                                {items.map((item, index) => (
                                    <Box
                                        key={index}
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "space-between",
                                            border: "1px solid #EAEAEA",
                                            borderRadius: "4px",
                                            padding: "8px 10px",
                                        }}
                                    >
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                                            <Box
                                                sx={{
                                                    width: 12,
                                                    height: 12,
                                                    borderRadius: "50%",
                                                    backgroundColor: item.color,
                                                }}
                                            />
                                            <Typography
                                                sx={{
                                                    fontSize: "14px",
                                                    fontWeight: 400,
                                                    fontFamily: "Outfit",
                                                }}
                                            >
                                                {item.label}
                                            </Typography>
                                        </Box>

                                        <Box
                                            sx={{
                                                minWidth: 40,
                                                height: 18,
                                                borderRadius: "5px",
                                                backgroundColor: "#E7E7EB",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                fontSize: "11px",
                                                fontWeight: 600,
                                            }}
                                        >
                                            {String(item.value).padStart(2, "0")}
                                        </Box>
                                    </Box>
                                ))}
                            </Stack>
                        </Box>
                        <Box
                            sx={{
                                width: "60%",
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                            }}
                        >
                            <HighchartsReact highcharts={Highcharts} options={options} />
                        </Box>
                    </Box>
                )}
            </Box>
            {
                isPopup && (
                    <LeaseListPopUp
                        header={header}
                        open={isPopup}
                        handleClose={handleClose}
                        filter={status}
                    />
                )
            }
        </>
    );
};

export default ActiveLeadsMixByTemperature;
